import discountDayGetter from '../valueGetters/discountDayGetter';
import discountMonthGetter from '../valueGetters/discountMonthGetter';

import currencyFormatter from '../formatters/priceFormatter';
import percentFormatter from '../formatters/percentFormatter';

import { getClassForAgGridCell } from '../../../helpers/cellHelpers';

import {
  Nullable,
  PricingSheetRow,
  RoundingBasis,
} from '../../../../../shared/types';

import { dayPriceGetter } from '../valueGetters/dayPriceGetter';
import {
  CellClassParams,
  ICellRendererParams,
  CellStyle,
  ValueGetterParams,
} from 'ag-grid-community';
import CommentRenderer from '../Renderers/CommentRenderer';
import proposalDayPriceGetter from '../valueGetters/proposalDayPriceGetter';
import proposalMonthPriceGetter from '../valueGetters/proposalMonthPriceGetter';
import { monthPriceGetter } from '../valueGetters/monthPriceGetter';
import { GridColumnProps } from './ColumnsBase';
import priceThresholdGetter from '../valueGetters/priceThresholdGetter';
import { getCatClassColDef } from '../helpers/getCatClassColDef';
import { PRICE_FACTOR } from '../../../../../shared/constants';

interface OptionalColumnProps {
  hideInspectComment: boolean;
  roundingBasisSheet: RoundingBasis;
}

const InspectColumnsBoth = ({
  classes,
  gridGroupingValues,
  optionalColumnProps,
}: GridColumnProps<OptionalColumnProps>) => {
  const columns = [
    ...getCatClassColDef(gridGroupingValues),
    {
      field: 'rentalDaysDay',
      headerName: 'Vuokrapäivät PV',
      filter: 'agNumberColumnFilter',
      editable: false,
      valueFormatter: (params: { value: Nullable<number> }) => {
        return (params.value ?? 0) + ' pv';
      },
      headerTooltip: 'Asiakkaan vuokrapäivät viimeisen vuoden ajalta',
      cellStyle: {
        backgroundColor: '#fafafa',
        borderLeft: '1px solid #ccc',
      } as CellStyle,
      suppressColumnsToolPanel: false,
      hide:
        localStorage.getItem('columnVisibility_rentalDaysDay') ===
        'false',
    },
    {
      field: 'proposalDayPrice',
      headerName: 'EhdotusPVHinta',
      filter: 'agNumberColumnFilter',
      valueGetter: proposalDayPriceGetter,
      valueFormatter: currencyFormatter,
      headerTooltip: 'Työkalun ehdottama hinta päivähinnaksi',
      cellStyle: {
        backgroundColor: '#fafafa',
        borderLeft: '1px solid #ccc',
      } as CellStyle,
    },
    {
      field: 'discountDay',
      headerName: 'Alennus% (PV)',
      filter: 'agNumberColumnFilter',
      valueGetter: discountDayGetter,
      valueFormatter: percentFormatter,
      headerTooltip: 'Päivähinta verrattuna ehdotushintaan',
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'discountDay',
        );
      },
      cellStyle: {
        borderLeft: '1px solid #ccc',
      },
    },
    {
      field: 'dayPrice',
      headerName: 'PVHinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<PricingSheetRow>) =>
        dayPriceGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
        ),
      valueFormatter: currencyFormatter,
      headerTooltip: 'Tuotteen päivähinta',
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'dayPrice',
        );
      },
      cellStyle: () => {
        return {
          color: '#000',
          borderLeft: '1px solid #ccc',
        } as CellStyle;
      },
    },
    {
      field: 'estimatedYieldDay',
      headerName: 'Laskennallinen tuotto PV',
      filter: 'agNumberColumnFilter',
      editable: false,

      valueFormatter: (params: {
        value: Nullable<number>;
        data: PricingSheetRow | undefined;
      }) => {
        return (
          Number(
            (params.data?.estimatedYieldDay ?? 0) / PRICE_FACTOR,
          ).toFixed(2) + ' €'
        );
      },
      headerTooltip: 'Asiakkaan vuokrapäivät viimeisen vuoden ajalta',
      suppressColumnsToolPanel: false,
      hide:
        localStorage.getItem('columnVisibility_estimatedYieldDay') ===
        'false',
    },
    {
      field: 'rentalDaysMonth',
      headerName: 'Vuokrapäivät KK',
      filter: 'agNumberColumnFilter',
      editable: false,
      valueFormatter: (params: { value: Nullable<number> }) => {
        return (params.value ?? 0) + ' pv';
      },
      headerTooltip: 'Asiakkaan vuokrapäivät viimeisen vuoden ajalta',
      cellStyle: {
        backgroundColor: '#fafafa',
        borderLeft: '1px solid #ccc',
      } as CellStyle,
      suppressColumnsToolPanel: false,
      hide:
        localStorage.getItem('columnVisibility_rentalDaysMonth') ===
        'false',
    },
    {
      field: 'priceThreshold',
      headerName: 'PV->KK kerroin',
      filter: 'agNumberColumnFilter',
      valueGetter: priceThresholdGetter,
      headerTooltip:
        'Kuinka moninkertainen kuukausihinta on verrattuna päivähintaan?',
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'priceThreshold',
        );
      },
      cellStyle: () => {
        return {
          color: '#000',
          borderRight: '1px solid #ccc',
        } as CellStyle;
      },
    },
    {
      field: 'proposalMonthPrice',
      headerName: 'EhdotusKKHinta',
      filter: 'agNumberColumnFilter',
      valueGetter: proposalMonthPriceGetter,
      valueFormatter: currencyFormatter,
      headerTooltip: 'Työkalun ehdottama hinta kuukausihinnaksi',
      cellStyle: {
        backgroundColor: '#fafafa',
        borderLeft: '1px solid #ccc',
      } as CellStyle,
    },
    {
      field: 'discountMonth',
      headerName: 'Alennus% (KK)',
      filter: 'agNumberColumnFilter',
      valueGetter: discountMonthGetter,
      valueFormatter: percentFormatter,
      headerTooltip: 'Kuukausihinta verrattuna ehdotushintaan',
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'discountMonth',
        );
      },
      cellStyle: () => {
        return {
          borderLeft: '1px solid #ccc',
        } as CellStyle;
      },
    },
    {
      field: 'monthPrice',
      headerName: 'KKHinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<PricingSheetRow>) =>
        monthPriceGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
        ),
      valueFormatter: currencyFormatter,
      headerTooltip: 'Tuotteen kuukausihinta',
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'monthPrice',
        );
      },
      cellStyle: () => {
        return {
          color: '#000',
          borderLeft: '1px solid #ccc',
          borderRight: '1px solid #ccc',
        } as CellStyle;
      },
    },
    {
      field: 'estimatedYieldMonth',
      headerName: 'Laskennallinen tuotto KK',
      filter: 'agNumberColumnFilter',
      editable: false,
      valueFormatter: (params: {
        value: Nullable<number>;
        data: PricingSheetRow | undefined;
      }) => {
        return (
          Number(
            (params.data?.estimatedYieldMonth ?? 0) / PRICE_FACTOR,
          ).toFixed(2) + ' €'
        );
      },
      headerTooltip: 'Asiakkaan vuokrapäivät viimeisen vuoden ajalta',
      suppressColumnsToolPanel: false,
      hide:
        localStorage.getItem(
          'columnVisibility_estimatedYieldMonth',
        ) === 'false',
    },
    {
      field: 'comment',
      headerName: 'Kommentti',
      minWidth: 200,
      hide: optionalColumnProps.hideInspectComment,
      cellRenderer: (params: ICellRendererParams) => {
        if (params?.data) {
          return <CommentRenderer row={params?.data} />;
        }
        return <></>;
      },
    },
  ];
  return columns;
};

export default InspectColumnsBoth;
